import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import Comission from 'services/Comission'
import Worksheet from 'services/Worksheet'
import Document from 'services/Document'
import General from 'services/General'
import BusinessNavbar from 'components/Navbars/BusinessNavbar'
import ItemTabs from 'components/ItemTabs'
import ProfessionalCreditList from 'components/Lists/ProfessionalCreditList'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import Logo from 'icons/logo-cutilei-simple.png'
import { FaFileExcel, FaFilePdf } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  LabelRow,
  EarningContainer,
  ProfessionalContainer,
  ProfessionalLabelRow,
  Image,
} from './styles'

import {
  Container,
  InfoText,
  InfoContainer,
  Row,
  Info,
  SubTitle,
  MoneyTextMask,
  ListContainer,
} from '../../styles'

export default function Total() {
  const location = useLocation()
  const navigate = useNavigate()

  const {
    business,
    dateRange = [],
    earningType = {},
    category,
    totalEarnings = 0,
    professionals = [],
    businessEarnings = 0,
    professionalEarnings = 0,
    selectedProfessional = {},
  } = location.state ?? {}

  useEffect(() => {
    if (!location.state) navigate('/business/financial', { replace: true })
  }, [location.state, navigate])

  const startDate = dateRange[0]
  const endDate = dateRange[1]
  const allEarningsSelected = earningType.value === 'all_earnings'
  const allProfessionalsSelected =
    selectedProfessional.value === 'all_professionals'
  const professionalLabel = allProfessionalsSelected
    ? 'dos profissionais'
    : 'do profissional'
  const [loading, setLoading] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [earningsByPeriod, setEarningsByPeriod] = useState(
    location.state?.earningsByPeriod ?? [],
  )
  const [sortedEarnings, setSortedEarnings] = useState({})
  const [bonuses, setBonuses] = useState([])
  const [discounts, setDiscounts] = useState([])
  const [bonusTotal, setBonusTotal] = useState(0)
  const [discountTotal, setDiscountTotal] = useState(0)
  const [earningOption, setEarningOption] = useState({
    label: 'Serviços',
    value: 'services',
  })
  const [typeOption, setTypeOption] = useState(
    allProfessionalsSelected
      ? { label: 'Total por profissional', value: 'professionals' }
      : allEarningsSelected
        ? { label: 'Comandas do salão', value: 'bill' }
        : earningType,
  )
  const token = AuthService.getToken()
  const businessId = AuthService.getBusinessId()
  const canViewTotalEarnings = AuthService.hasPermission('financial.total_earnings')
  const canViewBusinessEarnings = AuthService.hasPermission('financial.business_earnings')

  const requestConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }

  const TYPE_OPTION_LIST = [
    allProfessionalsSelected && {
      label: 'Total por profissional',
      value: 'professionals',
    },
    ['all_earnings', 'bill'].includes(earningType.value) && {
      label: 'Comandas do salão',
      value: 'bill',
    },
    ['all_earnings', 'cutilei'].includes(earningType.value) && {
      label: 'Agendamentos Cutilei',
      value: 'cutilei',
    },
  ].filter((o) => o)

  const EARNING_OPTION_LIST = [
    { label: 'Serviços', value: 'services' },
    { label: 'Produtos', value: 'products' },
    { label: 'Pacotes', value: 'bundles' },
    !allProfessionalsSelected && { label: 'Caixinhas', value: 'tips' },
    bonuses.length > 0 && !allProfessionalsSelected && { label: 'Bônus', value: 'bonuses' },
    discounts.length > 0 && !allProfessionalsSelected && { label: 'Descontos', value: 'discounts' },
  ].filter((o) => o)

  useEffect(() => {
    handleEarningOptionSelected()
  }, [typeOption, earningOption, earningsByPeriod])

  useEffect(() => {
    setBonuses(earningsByPeriod.filter((earning) => earning.type === 'bonus'))
    setDiscounts(earningsByPeriod.filter((earning) => earning.type === 'discount'))
  }, [earningsByPeriod])

  useEffect(() => {
    setBonusTotal(bonuses.reduce((total, bonus) => total + bonus.value, 0))
  }, [bonuses])

  useEffect(() => {
    setDiscountTotal(discounts.reduce((total, bonus) => total + bonus.value, 0))
  }, [discounts])

  const getBonusesAndDiscounts = async () => {
    try {
      setLoading(true)

      if (allProfessionalsSelected && allEarningsSelected) {
        const dateFilters = `&start_date=${startDate}&end_date=${endDate}`

        const [{ data: bonuses }, { data: discounts }] = await Promise.all([
          cutileiApi.get(
            `/businesses/${businessId}/credits?professional_id=${selectedProfessional.value}` +
              dateFilters,
            requestConfig,
          ),
          cutileiApi.get(
            `/businesses/${businessId}/debts?professional_id=${selectedProfessional.value}` +
              dateFilters,
            requestConfig,
          ),
        ])

        const formattedDiscounts = discounts.map((d) => ({
          ...d,
          value: d.value * -1,
        }))
        setBonuses(bonuses)
        setDiscounts(formattedDiscounts)
        return { bonuses, discounts: formattedDiscounts }
      } else {
        return { bonuses, discounts }
      }
    } catch (error) {
      logApiError(error)
    } finally {
      setLoading(false)
    }
  }

  const handleEarningOptionSelected = async () => {
    let sortedEarnings = {}
    if (typeOption.value === 'cutilei') {
      const cutileiEarnings = earningsByPeriod.filter((earning) => earning.type === 'cutilei')

      sortedEarnings = {
        cutilei: sortEarningsByType(cutileiEarnings, cutileiEarnings).map(
          (e) => ({
            ...e,
            cutilei_schedule: true,
          }),
        ),
      }
    } else if (typeOption.value === 'professionals') {
      const { bonuses, discounts } = await getBonusesAndDiscounts()
      const bonusTotal = bonuses.reduce((total, bonus) => total + bonus.value, 0)
      const discountTotal = discounts.reduce((total, bonus) => total + bonus.value, 0)

      sortedEarnings = {
        professionals: [
          {
            name: 'TOTAL',
            totalEarnings,
            professionalEarnings,
            ...(bonuses.length > 0 && { bonusTotal }),
            ...(discounts.length > 0 && { discountTotal }),
            professionalTotalEarnings:
              professionalEarnings + bonusTotal + discountTotal,
            businessEarnings,
          },
        ].concat(
          professionals.map((professional) => {
            const professionalBonuses = bonuses.filter((b) => b.professional.id === professional.id)
            const professionalDiscounts = discounts.filter((d) => d.professional.id === professional.id)

            const professionalBonusTotal = professionalBonuses.reduce(
              (total, bonus) => total + bonus.value, 0
            )

            const professionalDiscountTotal = professionalDiscounts.reduce(
              (total, discount) => total + discount.value, 0
            )

            const totalEarnings = Comission.getTotalEarnings(
              earningsByPeriod,
              earningType,
              category,
              professional,
            )

            const professionalEarnings = Comission.getProfessionalEarnings(
              earningsByPeriod,
              earningType,
              category,
              professional,
            )

            const professionalTotalEarnings =
              professionalEarnings +
              professionalBonusTotal +
              professionalDiscountTotal

            const businessEarnings = Comission.getBusinessEarnings(
              earningsByPeriod,
              earningType,
              category,
              professional,
            )

            return {
              name: professional.nickname,
              totalEarnings,
              professionalEarnings,
              ...(bonuses.length > 0 && { bonusTotal: professionalBonusTotal }),
              ...(discounts.length > 0 && {
                discountTotal: professionalDiscountTotal,
              }),
              professionalTotalEarnings,
              businessEarnings,
            }
          }),
        ),
      }
    } else {
      const businessEarnings = earningsByPeriod.filter((earning) => earning.type === 'bill')

      const schedules = [],
        services = [],
        products = [],
        bundles = [],
        tips = []

      const serviceFilteredBills = businessEarnings
        .filter((bill) => bill.services?.length > 0 || bill.schedules?.length > 0)
        .map((bill) => ({ ...bill, products, bundles, tips }))

      const productFilteredBills = businessEarnings
        .filter((bill) => bill.products.length > 0)
        .map((bill) => ({ ...bill, schedules, services, bundles, tips }))

      const bundleFilteredBills = businessEarnings
        .filter((bill) => bill.bundles.length > 0)
        .map((bill) => ({ ...bill, schedules, services, products, tips }))

      const tipFilteredBills = businessEarnings
        .filter((bill) => bill.tips.length > 0)
        .map((bill) => ({ ...bill, schedules, services, products, bundles }))

      const serviceEarnings = businessEarnings.reduce(
        (acc, bill) =>
          acc.concat([
            ...bill.services
              .filter((s) => !s.cutilei_service)
              .filter((s) => category ? s.category.id === category.value : true),
            ...bill.schedules
              .filter((s) => !s.cutilei_schedule)
              .filter((s) => category ? s.service.category.id === category.value : true),
          ]),
        [],
      )

      const productEarnings = businessEarnings.reduce((acc, bill) => acc.concat(bill.products), [])
      const bundleEarnings = businessEarnings.reduce((acc, bill) => acc.concat(bill.bundles), [])
      const tipEarnings = businessEarnings.reduce((acc, bill) => acc.concat(bill.tips), [])
      const filteredBonuses = earningsByPeriod.filter((earning) => earning.type === 'bonus')
      const filteredDiscounts = earningsByPeriod.filter((earning) => earning.type === 'discount')

      sortedEarnings = {
        services: sortEarningsByType(serviceEarnings, serviceFilteredBills),
        products: sortEarningsByType(productEarnings, productFilteredBills),
        bundles: sortEarningsByType(bundleEarnings, bundleFilteredBills),
        tips: sortEarningsByType(tipEarnings, tipFilteredBills),
        bonuses: filteredBonuses,
        discounts: filteredDiscounts,
      }
    }
    setSortedEarnings(sortedEarnings)
  }

  const sortEarningsByType = (
    typeEarnings,
    typeFilteredBills,
    type = typeOption,
  ) => [
    {
      name: 'TOTAL',
      qtd: typeEarnings.reduce(sumItemsAmount, 0),
      totalEarnings: Comission.getTotalEarnings(typeFilteredBills, type, category),
      professionalEarnings: Comission.getProfessionalEarnings(typeFilteredBills, type, category),
      businessEarnings: Comission.getBusinessEarnings(typeFilteredBills, type, category),
    },
    ...General.groupBy(
      typeEarnings.map((e) => ({
        ...e,
        name: Comission.getItemLabel(e),
      })),
      'name',
    )
      .map((earnings) => ({
        name: earnings[0].name,
        qtd: earnings.reduce(sumItemsAmount, 0),
        totalEarnings: earnings.filter(billedItems).reduce(sumItemsTotal, 0),
        professionalEarnings: earnings.reduce(sumProfessionalItemsTotal, 0),
        businessEarnings: earnings
          .filter(billedItems)
          .reduce(sumBusinessItemsTotal, 0),
      }))
      .sort((a, b) => b.qtd - a.qtd),
  ]

  const billedItems = (item) => Comission.isBilledItem(item)
  const sumItemsAmount = (total, earning) => total + (earning.amount || 1)
  const sumItemsTotal = (total, item) => total + Comission.getItemTotalEarnings(item)
  const sumBusinessItemsTotal = (total, item) => total + Comission.getItemBusinessEarnings(item)
  const sumProfessionalItemsTotal = (total, item) => total + Comission.getItemProfessionalEarnings(item)

  const handleDeleteCredit = (credit) => {
    const index = EARNING_OPTION_LIST.findIndex((o) => o && o.value === earningOption.value)
    const filteredEarnings = earningsByPeriod.filter((e) => e.id !== credit.id)

    setEarningsByPeriod(filteredEarnings)

    if (!filteredEarnings.some((e) => e.type === credit.type))
      setEarningOption(EARNING_OPTION_LIST[index - 1])
  }

  const handleExportSheet = () =>
    Worksheet.createDetailedEarningsSheet({
      dateRange,
      earningOptionList: EARNING_OPTION_LIST,
      typeOptionList: TYPE_OPTION_LIST,
      typeOption,
      selectedProfessional,
      sortedEarnings,
      bonuses,
      discounts,
    })

  const handleExportPDF = (isSimplified = false) => {
    const cutileiEarnings = earningsByPeriod.filter((e) => e.type === 'cutilei')

    const totalEarnings = {
      cutilei: sortEarningsByType(cutileiEarnings, cutileiEarnings, {
        value: 'cutilei',
      }),
      ...sortedEarnings,
    }

    Document.downloadProfessionalEarningsPDF({
      business,
      dateRange,
      typeOption,
      professional: selectedProfessional,
      sortedEarnings: totalEarnings,
      isSimplified,
      setDownloading,
    })
  }

  const handleExportComissionPaymentPDF = () => {
    const report = Comission.getComissionPaymentReport(
      earningsByPeriod,
      earningType,
      category,
      { id: selectedProfessional.value },
    )

    Document.downloadComissionPaymentPDF({
      business,
      dateRange,
      professional: selectedProfessional,
      report,
      setDownloading,
    })
  }

  return (
    <Container>
      <BusinessNavbar/>
      {loading ? (
        <Loading style={{ marginTop: 30 }} />
      ) : (
        <>
          <InfoContainer style={{ alignItems: 'center' }}>
            <SubTitle style={{ marginBottom: 7 }}>
              Total do período (
              {DateTime.fromISO(startDate).toFormat('dd/MM/yyyy')} -
              {DateTime.fromISO(endDate).toFormat(' dd/MM/yyyy')}) -{' '}
              {selectedProfessional.label}
            </SubTitle>
            <Info style={{ alignItems: 'center', height: 'auto' }}>
              {canViewTotalEarnings && (
                <Row>
                  <InfoText>Faturamento bruto:</InfoText>
                  <MoneyTextMask value={totalEarnings}/>
                </Row>
              )}
              <Row>
                <InfoText>Comissão {professionalLabel}:</InfoText>
                <MoneyTextMask value={professionalEarnings}/>
              </Row>
              {bonuses.length > 0 && (
                <Row>
                  <InfoText>Bônus {professionalLabel}:</InfoText>
                  <MoneyTextMask value={bonusTotal ?? 0}/>
                </Row>
              )}
              {discounts.length > 0 && (
                <Row>
                  <InfoText>Descontos {professionalLabel}:</InfoText>
                  <MoneyTextMask value={discountTotal ?? 0}/>
                </Row>
              )}
              {(bonuses.length > 0 || discounts.length > 0) && (
                <Row>
                  <InfoText>Total {professionalLabel}:</InfoText>
                  <MoneyTextMask
                    value={
                      professionalEarnings +
                      (bonusTotal ?? 0) +
                      (discountTotal ?? 0)
                    }
                  />
                </Row>
              )}
              {canViewBusinessEarnings && (
                <Row>
                  <SubTitle>Total do salão:</SubTitle>
                  <MoneyTextMask value={businessEarnings} />
                </Row>
              )}
            </Info>
          </InfoContainer>
          {(allEarningsSelected || allProfessionalsSelected) && (
            <ItemTabs
              tabs={TYPE_OPTION_LIST}
              selectedTab={typeOption.value}
              onTabChange={(value) =>
                setTypeOption(TYPE_OPTION_LIST.find((o) => o.value === value))
              }
              containerStyle={{ paddingTop: 0 }}
              tabWidth={180}
            />
          )}
          {typeOption.value === 'bill' && (
            <ItemTabs
              tabs={EARNING_OPTION_LIST}
              selectedTab={earningOption.value}
              onTabChange={(value) =>
                setEarningOption(
                  EARNING_OPTION_LIST.find((o) => o.value === value),
                )
              }
              containerStyle={{ paddingTop: 0 }}
            />
          )}
          <Row>
            <SecondaryButton
              label='Gerar planilha'
              onClick={handleExportSheet}
              leftIcon={<FaFileExcel size={14} color='#FFC100' style={{ marginBottom: 2 }}/>}
              width={200}
            />
            {selectedProfessional.value !== 'all_professionals' &&
              allEarningsSelected && (
                <>
                  <SecondaryButton
                    label='Gerar PDF completo'
                    onClick={() => handleExportPDF(false)}
                    leftIcon={<FaFilePdf size={14} color='#FFC100' style={{ marginBottom: 2 }}/>}
                    disabled={downloading}
                    width={200}
                  />
                  <SecondaryButton
                    label='Gerar PDF simplificado'
                    onClick={() => handleExportPDF(true)}
                    leftIcon={<FaFilePdf size={14} color='#FFC100' style={{ marginBottom: 2 }}/>}
                    disabled={downloading}
                    width={200}
                  />
                </>
              )}
            {!allProfessionalsSelected && (
              <SecondaryButton
                label='Gerar recibo de comissões'
                onClick={handleExportComissionPaymentPDF}
                leftIcon={<FaFilePdf size={14} color='#FFC100' style={{ marginBottom: 2 }}/>}
                disabled={downloading}
                width={220}
              />
            )}
          </Row>
          <ListContainer>
            {['bonuses', 'discounts'].includes(earningOption.value) ? (
              <ProfessionalCreditList
                credits={sortedEarnings[earningOption.value]}
                type={earningOption.value === 'bonuses' ? 'bonus' : 'discount'}
                onEdit={(credit) => {
                  setEarningsByPeriod(
                    earningsByPeriod.map((e) =>
                      e.id === credit.id ? { ...credit } : e,
                    ),
                  )
                }}
                onDelete={handleDeleteCredit}
                styles={{ width: 960, minWidth: 840 }}
              />
            ) : typeOption.value === 'professionals' ? (
              <>
                <ProfessionalLabelRow
                  bonuses={bonuses}
                  discounts={discounts}
                  showTotalEarnings={canViewTotalEarnings}
                  showBusinessEarnings={canViewBusinessEarnings}
                >
                  <SubTitle>Profissional</SubTitle>
                  {canViewTotalEarnings && <SubTitle>Total bruto</SubTitle>}
                  <SubTitle>Comissões</SubTitle>
                  {bonuses.length > 0 && <SubTitle>Bônus</SubTitle>}
                  {discounts.length > 0 && <SubTitle>Descontos</SubTitle>}
                  <SubTitle>Total do profissional</SubTitle>
                  {canViewBusinessEarnings && (
                    <SubTitle>Total do salão</SubTitle>
                  )}
                </ProfessionalLabelRow>
                {sortedEarnings[typeOption.value]?.map(
                  (professional, index) => {
                    const {
                      name,
                      totalEarnings,
                      professionalEarnings,
                      bonusTotal,
                      discountTotal,
                      professionalTotalEarnings,
                      businessEarnings,
                    } = professional

                    return (
                      <ProfessionalContainer
                        key={index}
                        bonuses={bonuses}
                        discounts={discounts}
                        showTotalEarnings={canViewTotalEarnings}
                        showBusinessEarnings={canViewBusinessEarnings}
                      >
                        <SubTitle>{name}</SubTitle>
                        {canViewTotalEarnings && (
                          <MoneyTextMask value={totalEarnings}/>
                        )}
                        <MoneyTextMask value={professionalEarnings}/>
                        {bonuses.length > 0 && (
                          <MoneyTextMask value={bonusTotal ?? 0}/>
                        )}
                        {discounts.length > 0 && (
                          <MoneyTextMask value={discountTotal ?? 0}/>
                        )}
                        <MoneyTextMask value={professionalTotalEarnings}/>
                        {canViewBusinessEarnings && (
                          <MoneyTextMask value={businessEarnings}/>
                        )}
                      </ProfessionalContainer>
                    )
                  },
                )}
              </>
            ) : (
              <>
                <LabelRow
                  showTotalEarnings={canViewTotalEarnings}
                  showBusinessEarnings={canViewBusinessEarnings}
                >
                  <SubTitle>{earningOption.label}</SubTitle>
                  <SubTitle>Qtd.</SubTitle>
                  {canViewTotalEarnings && <SubTitle>Total bruto</SubTitle>}
                  <SubTitle>Comissões</SubTitle>
                  {canViewBusinessEarnings && (
                    <SubTitle>Total do salão</SubTitle>
                  )}
                </LabelRow>
                {sortedEarnings[
                  typeOption.value === 'bill'
                    ? earningOption.value
                    : typeOption.value
                ]?.map((earning, index) => (
                  <EarningContainer
                    key={index}
                    showTotalEarnings={canViewTotalEarnings}
                    showBusinessEarnings={canViewBusinessEarnings}
                  >
                    <Row>
                      {earning.cutilei_schedule && (
                        <Image src={Logo} alt="Service Cutilei"/>
                      )}
                      <SubTitle>
                        {earning?.name ??
                          earning?.service?.name ??
                          earning?.description}
                      </SubTitle>
                    </Row>
                    <SubTitle>{earning.qtd}</SubTitle>
                    {canViewTotalEarnings && (
                      <MoneyTextMask value={earning.totalEarnings}/>
                    )}
                    <MoneyTextMask value={earning.professionalEarnings}/>
                    {canViewBusinessEarnings && (
                      <MoneyTextMask value={earning.businessEarnings}/>
                    )}
                  </EarningContainer>
                ))}
              </>
            )}
          </ListContainer>
        </>
      )}
    </Container>
  )
}
